import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Zap, Mic, Globe, BarChart3, Shield, Play, ArrowRight, Check, Youtube, Instagram, Twitter } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_video-forge-417/artifacts/8amlg66h_Logo%20futuriste%20de%20ViralSynth.com.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const features = [
  { icon: Mic, title: "Entree vocale", desc: "Decrivez votre projet a la voix. L'IA comprend votre niche, votre ton, votre audience." },
  { icon: Globe, title: "Multi-plateformes", desc: "YouTube, Instagram, TikTok, X. Publication automatique sur toutes vos chaines." },
  { icon: Zap, title: "Scripts IA", desc: "GPT-5.2 ecrit des scripts viraux optimises pour chaque plateforme." },
  { icon: Shield, title: "Cles chiffrees", desc: "Vos credentials API sont stockes avec un chiffrement de niveau bancaire." },
  { icon: BarChart3, title: "Optimisation continue", desc: "L'IA analyse vos performances et ameliore chaque video automatiquement." },
  { icon: Play, title: "100% automatique", desc: "Un clic suffit. La plateforme genere, publie et optimise sans intervention." },
];

const plans = [
  { id: "basic", name: "Starter", price: "49", credits: "1 000", videos: "10", frequency: "2-3/semaine", popular: false },
  { id: "pro", name: "Pro", price: "99", credits: "2 500", videos: "25", frequency: "1/jour", popular: true },
  { id: "max", name: "Business", price: "249", credits: "6 000", videos: "60", frequency: "2/jour", popular: false },
  { id: "enterprise", name: "Enterprise", price: "699", credits: "20 000", videos: "200", frequency: "Sur mesure", popular: false },
];

const steps = [
  { num: "01", title: "Decrivez votre projet", desc: "Parlez ou tapez. L'IA comprend votre niche, votre ton et votre audience cible." },
  { num: "02", title: "Connectez vos chaines", desc: "YouTube, Instagram, TikTok, X. Entrez vos cles API une seule fois, stockees chiffrees." },
  { num: "03", title: "L'IA prend le relais", desc: "Generation automatique, publication planifiee, optimisation continue. Chaque jour." },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 glass" data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <img src={LOGO_URL} alt="ViralSynth" className="w-52 h-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-white/60 hover:text-white transition-colors">Fonctionnalites</a>
            <a href="#pricing" className="text-sm text-white/60 hover:text-white transition-colors">Tarifs</a>
            <a href="#how-it-works" className="text-sm text-white/60 hover:text-white transition-colors">Comment ca marche</a>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate('/dashboard')} className="btn-primary text-sm" data-testid="go-to-dashboard-btn">
                Mon Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="btn-outline text-sm" data-testid="login-nav-btn">
                  Connexion
                </button>
                <button onClick={() => navigate('/register')} className="btn-accent text-sm" data-testid="register-nav-btn">
                  Demarrer
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3379932/pexels-photo-3379932.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF3300] mb-6 animate-fade-in-up">
              La premiere usine a videos IA
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.1] mb-6 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
              Votre chaine video IA,<br />
              <span className="font-medium">en autopilote.</span>
            </h1>
            <p className="text-base lg:text-lg text-white/60 max-w-xl mb-10 leading-relaxed animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              Decrivez votre niche. L'IA cree, publie et optimise vos videos.
              Chaque jour. Sur toutes les plateformes.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              <button
                onClick={() => navigate(user ? '/projects/new' : '/register')}
                className="btn-accent text-base px-8 py-4 flex items-center gap-2"
                data-testid="hero-cta-btn"
              >
                Creer ma chaine <ArrowRight className="w-5 h-5" />
              </button>
              <a href="#how-it-works" className="btn-outline text-base px-8 py-4 flex items-center gap-2">
                <Play className="w-5 h-5" /> Voir la demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-16 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.2em] uppercase text-center text-white/40 mb-8">Publication automatique sur</p>
          <div className="flex items-center justify-center gap-12 flex-wrap">
            <Youtube className="w-8 h-8 text-white/30 hover:text-red-500 transition-colors" />
            <Instagram className="w-8 h-8 text-white/30 hover:text-pink-500 transition-colors" />
            <Twitter className="w-8 h-8 text-white/30 hover:text-blue-400 transition-colors" />
            <svg className="w-8 h-8 text-white/30 hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.82.12v-3.5a6.37 6.37 0 00-.82-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.14a8.16 8.16 0 003.76.92V6.69z"/>
            </svg>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24 md:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF3300] mb-4">Processus</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-16">
            Comment ca marche
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.num} className="card-surface rounded-lg p-8 md:p-12 group">
                <span className="text-5xl font-heading font-light text-white/10 group-hover:text-[#FF3300]/30 transition-colors">{step.num}</span>
                <h3 className="font-heading text-xl font-medium mt-4 mb-3">{step.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 md:py-32 border-t border-white/5" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF3300] mb-4">Fonctionnalites</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-16">
            Tout est automatique.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="card-surface rounded-lg p-8 group" style={{animationDelay: `${i * 0.1}s`}}>
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center mb-5 group-hover:bg-[#FF3300]/10 transition-colors">
                    <Icon className="w-5 h-5 text-white/60 group-hover:text-[#FF3300] transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg font-medium mb-2">{f.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 md:py-32 border-t border-white/5" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF3300] mb-4">Tarifs</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium">
              Tarifs simples, resultats exponentiels.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-lg p-8 transition-all duration-300 ${
                  plan.popular
                    ? 'bg-[#121212] border-2 border-white/20 scale-[1.02]'
                    : 'card-surface'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#FF3300] mb-4 block">Populaire</span>
                )}
                <h3 className="font-heading text-xl font-medium mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="font-heading text-4xl font-light">${plan.price}</span>
                  <span className="text-white/40 text-sm">/mois</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-[#34C759]" /> {plan.videos} videos/mois
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-[#34C759]" /> {plan.credits} credits inclus
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-[#34C759]" /> Frequence: {plan.frequency}
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-[#34C759]" /> Multi-plateformes
                  </li>
                </ul>
                <button
                  onClick={() => navigate(user ? '/pricing' : '/register')}
                  className={`w-full py-3 rounded-md text-sm font-medium transition-all ${
                    plan.popular ? 'btn-accent' : 'btn-outline'
                  }`}
                  data-testid={`plan-select-${plan.id}`}
                >
                  Choisir {plan.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-6">
            Pret a lancer votre chaine IA ?
          </h2>
          <p className="text-base text-white/50 mb-10 max-w-xl mx-auto">
            Rejoignez les createurs qui automatisent leur production video avec l'intelligence artificielle.
          </p>
          <button
            onClick={() => navigate(user ? '/projects/new' : '/register')}
            className="btn-accent text-base px-10 py-4 inline-flex items-center gap-2"
            data-testid="cta-start-btn"
          >
            Commencer gratuitement <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="ViralSynth" className="w-36 h-auto" />
          </div>
          <div className="flex items-center gap-4 text-xs text-white/30">
            <Link to="/privacy" className="hover:text-white/60 transition-colors" data-testid="footer-privacy-link">Confidentialite</Link>
            <span>&middot;</span>
            <Link to="/terms" className="hover:text-white/60 transition-colors" data-testid="footer-terms-link">Conditions</Link>
            <span>&middot;</span>
            <a href="mailto:info@viralsynth.com" className="hover:text-white/60 transition-colors">Contact</a>
            <span>&middot;</span>
            <span>&copy; 2026 ViralSynth</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
